// @ts-nocheck
//
// Full-screen view router (new_ui).
//
// Introduces an #app-main overlay that hosts the non-garden views
// (Tempio / Community / Profilo / Crea / Grazie). The immersive garden
// (#viewport) is "view 0": showView('garden') hides #app-main so the pannable
// garden is interactive again; switching to any other view shows #app-main and
// the matching #view-* section with a `pfade` transition.
//
// The router is purely presentational: it owns DOM visibility + nav active
// state. The actual data/logic lives in the feature modules, which render their
// markup into getViewContainer(view) and call showView(view) to reveal it.

export type AppView =
  | 'garden'
  | 'tempio'
  | 'community'
  | 'profilo'
  | 'crea'
  | 'grazie';

type OverlayView = Exclude<AppView, 'garden'>;

const VIEW_IDS: OverlayView[] = [
  'tempio',
  'community',
  'profilo',
  'crea',
  'grazie',
];

declare const lucide: any;

let currentView: AppView = 'garden';
let appMain: HTMLElement | null = null;
const viewEls: Partial<Record<OverlayView, HTMLElement>> = {};
const listeners = new Set<(view: AppView) => void>();

export function initViewRouter(): void {
  if (appMain) return;

  appMain = document.createElement('div');
  appMain.id = 'app-main';

  for (const id of VIEW_IDS) {
    const el = document.createElement('section');
    el.id = `view-${id}`;
    el.className = 'app-view';
    appMain.appendChild(el);
    viewEls[id] = el;
  }

  // Insert right after #viewport so it overlays the garden (z-index handles
  // the stacking vs the sidebar/modals).
  const viewport = document.getElementById('viewport');
  if (viewport && viewport.parentNode) {
    viewport.parentNode.insertBefore(appMain, viewport.nextSibling);
  } else {
    document.body.appendChild(appMain);
  }

  syncNavActive('garden');
  syncDockVisibility('garden');
}

/** The empty container for a given overlay view; feature modules render into it. */
export function getViewContainer(view: OverlayView): HTMLElement | null {
  if (!appMain) initViewRouter();
  return viewEls[view] ?? null;
}

export function getCurrentView(): AppView {
  return currentView;
}

export function onViewChange(cb: (view: AppView) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function showView(view: AppView): void {
  if (!appMain) initViewRouter();
  currentView = view;

  if (view === 'garden') {
    appMain?.classList.remove('is-open');
    for (const id of VIEW_IDS) viewEls[id]?.classList.remove('is-active');
  } else {
    appMain?.classList.add('is-open');
    for (const id of VIEW_IDS) {
      viewEls[id]?.classList.toggle('is-active', id === view);
    }
    // re-trigger the fade each time the same view is reopened
    const active = viewEls[view as OverlayView];
    if (active) {
      active.style.animation = 'none';
      // force reflow so the animation restarts
      void active.offsetWidth;
      active.style.animation = '';
      if (view === 'crea') {
        active.querySelector('.crea-body')?.scrollTo?.(0, 0);
      } else {
        active.scrollTo?.(0, 0);
      }
    }
  }

  syncNavActive(view);
  syncDockVisibility(view);
  if (typeof lucide !== 'undefined') lucide.createIcons();
  listeners.forEach((cb) => cb(view));
}

function syncNavActive(view: AppView): void {
  const chrome = document.getElementById('app-chrome');
  if (!chrome) return;
  chrome.querySelectorAll('[data-view]').forEach((btn) => {
    const active = btn.getAttribute('data-view') === view;
    btn.classList.toggle('is-active', active);
    if (active) btn.setAttribute('aria-current', 'page');
    else btn.removeAttribute('aria-current');
  });
}

function syncDockVisibility(view: AppView): void {
  const chrome = document.getElementById('app-chrome');
  if (!chrome) return;
  const hideDock = view === 'crea' || view === 'grazie';
  chrome.hidden = hideDock;
}
